// Route guards. Per frontend-design §1 Authorization rendering, the
// server is the authoritative permission check; these guards just
// choose what to render before the request lands so the UX matches
// the eventual server response.

import { Navigate } from 'react-router-dom';
import { usePrincipal } from '@/auth/PrincipalContext';
import {
  canViewAdminSurfaces,
  canViewDashboard,
  homeRouteFor,
  isMember,
} from '@/auth/permissions';
import type { ReactNode } from 'react';

export function RequireAdmin({ children }: { children: ReactNode }) {
  const principal = usePrincipal();
  // Member / PL hit a "page not found" experience (visibility 404) per
  // frontend-design §2 (admin surface is admin-only).
  if (!principal) return null;
  if (!canViewAdminSurfaces(principal)) {
    return <Navigate to="/not-found" replace />;
  }
  return <>{children}</>;
}

export function RequireDashboard({ children }: { children: ReactNode }) {
  const principal = usePrincipal();
  if (!principal) return null;
  if (!canViewDashboard(principal)) {
    return <Navigate to={homeRouteFor(principal)} replace />;
  }
  return <>{children}</>;
}

export function RequireNonMember({ children }: { children: ReactNode }) {
  const principal = usePrincipal();
  if (!principal) return null;
  if (isMember(principal)) {
    return <Navigate to={homeRouteFor(principal)} replace />;
  }
  return <>{children}</>;
}

export function HomeRedirect() {
  const principal = usePrincipal();
  if (!principal) return null;
  return <Navigate to={homeRouteFor(principal)} replace />;
}
